import React from 'react';
import { Redirect, useLocation } from 'react-router-dom';
import { useAuthReadiness } from 'hooks/useAuthReadiness';
import { OrganizationType } from 'modules/Common/utils/Utility';
import CgPageLoader from 'modules/Common/layout/CgPageLoader';
import PermissionsErrorState from 'modules/Common/permissions/PermissionsErrorState';
import PlanEndedState from 'modules/Common/permissions/PlanEndedState';
import {
  isProvisionalDenyAll,
  usePermissionsContext,
} from 'modules/Common/permissions/PermissionsProvider';
import { isWhitelabelBrand, useBranding } from 'modules/Common/hooks/useBranding';
import {
  hasFullPlatform,
  isFreeSupplier,
  isFreeSupplierPlan,
  SUPPLIER_HOME_PATH,
} from 'modules/FreeSupplier/planAccess';
import { isSupplierHomePath } from 'modules/FreeSupplier/routes';

// `/not-found` stays on this list, but its meaning changed: it is now only ever
// a GENUINE 404 (an unmatched URL), never an authorization destination —
// denials render in place via RouteAccessGate and no longer navigate here.
// Keeping it means a free supplier who follows a dead link sees the 404 rather
// than being silently bounced to the supplier home, which would hide the broken
// link. See docs/features/denial-never-navigates/00-OVERVIEW.md (N4, I3).
const isFreeSupplierRoute = (pathname) =>
  isSupplierHomePath(pathname) || pathname === '/team/manage' || pathname === '/not-found';

// The escape hatches. These three pages exist BECAUSE a workspace's plan has
// lapsed, is absent, or needs upgrading — so gating them on having a good plan
// is circular, and it bricked the exact users they were built for.
//
// The reported symptom: an operator whose trial expired saw "Unable to verify
// permissions" on /trial-expired. `/permissions/me` goes through the intercepted
// http client, and the trial-expiry interceptor (helpers/requests.js) does two
// things at once — sends the user to /trial-expired AND leaves that query
// failed. So they arrive precisely when `status === 'error'`, and the boundary
// below then refused to render the one page that could help them. The same
// applies to a settled payload with no `full_platform` entitlement.
//
// `requests.js` already treats /trial-expired and /pricing as redirect-loop
// breakers, which is corroborating evidence that these are meant to stay
// reachable with a dead plan.
const PLAN_ESCAPE_HATCHES = new Set(['/trial-expired', '/pricing', '/upgrade']);

// The org comes from useAuthReadiness (→ ActiveOrgContext), NOT useOrganization().
// The org-type check below is the switch that decides whether the operator plan
// rules apply at all, and `me` beside it is always the CURRENT org's payload.
// Reading the lagging hook there meant judging one org's payload by another
// org's rules, in both directions:
//   • operator → declarant: the hook still says `operator`, so the declarant's
//     payload gets run through operator plan checks it has no reason to satisfy
//     → PermissionsErrorState over the ENTIRE app (this boundary wraps the whole
//     route table, _routes.jsx:117-812). This is the false "no access" screen.
//   • declarant → operator: the hook still says `declarant`, so we bail early and
//     a free-tier workspace renders full-platform pages until the hook catches up.
// Both self-correct when the hook lands, which is why they read as flashes.
const WorkspacePlanBoundary = ({ children }) => {
  const { status: orgStatus, organization } = useAuthReadiness();
  const { me, status } = usePermissionsContext();
  const location = useLocation();
  const brand = useBranding();

  // Before anything else: the escape hatches are always reachable. Deliberately
  // ahead of the org-type and readiness checks — a user whose plan is dead must
  // not wait on, or be judged by, the very plan lookup that is failing.
  if (PLAN_ESCAPE_HATCHES.has(location.pathname)) return children;

  // Defer rather than guess while the active org is unknown. Near-unreachable in
  // practice (ActiveOrgContext is seeded synchronously at OrgBoundary mount and
  // this renders inside <SignedIn>), but guessing here picks the wrong ruleset.
  if (orgStatus === 'loading') return <CgPageLoader ariaLabel="Loading your workspace plan" />;
  // `no-org` lands here as `organization === null` → not an operator → pass through.
  if (organization?.publicMetadata?.type !== OrganizationType.Operator) return children;
  if (status === 'loading') return <CgPageLoader ariaLabel="Loading your workspace plan" />;
  if (status === 'error') return <PermissionsErrorState />;

  if (isFreeSupplierPlan(me)) {
    if (!isFreeSupplier(me)) return <PermissionsErrorState />;
    if (!isFreeSupplierRoute(location.pathname)) return <Redirect to={SUPPLIER_HOME_PATH} />;
    return children;
  }

  if (hasFullPlatform(me)) return children;
  if (me?.planLookupFailed === true || isProvisionalDenyAll(me)) return <PermissionsErrorState />;
  if (isWhitelabelBrand(brand)) return <PlanEndedState />;
  return <Redirect to="/trial-expired" />;
};

export default WorkspacePlanBoundary;
